import { ProductsPicker } from '@wix/bookings-products-picker';
import '@wix/bookings-products-picker/styles.css';
import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React, { FC, useEffect, useMemo } from 'react';
import settingsParams from '../../settingsParams';
import { getContent } from '../../../../utils/content/content';
import type { SelectedProduct, V3Product } from '../../../../utils/state/types';
import { enableWixSdkContext } from '../../../../utils/wixSdkContext/wixSdkContext';
import { classes, st } from './RelatedProducts.st.css';
import Text, { TextType } from '../../Text/Text';
import { TagName } from '../../../../utils/tagName.const';

const isProductOutOfStock = (product: V3Product): boolean =>
  product.inventory?.availabilityStatus === 'OUT_OF_STOCK';

interface RelatedProductsProps {
  products: V3Product[];
  selection: SelectedProduct[];
  onSelectionChange: (next: SelectedProduct[]) => void;
  onModalOpen: (productId: string) => void;
  isLoadingProductDetails: boolean;
  storesAppInstance?: string;
}

export const RelatedProducts: FC<RelatedProductsProps> = ({
  products,
  selection,
  onSelectionChange,
  onModalOpen,
  isLoadingProductDetails,
  storesAppInstance,
}) => {
  const handleSelectionChange = (next: SelectedProduct[]) => {
    onSelectionChange(next);
  };
  const { t } = useTranslation();
  const { isMobile, isSSR } = useEnvironment();
  useEffect(() => {
    enableWixSdkContext(isSSR, storesAppInstance);
  }, [isSSR, storesAppInstance]);
  const settings = useSettings();
  const title = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsTitle,
  });
  const subtitle = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsSubtitle,
  });
  const ctaLabel = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsAddToBookingLabel,
  });
  const maxItems = settings.get(settingsParams.maxRelatedProductsToDisplay);

  const inStockProducts = useMemo(
    () => products.filter((product) => !isProductOutOfStock(product)),
    [products],
  );

  if (!inStockProducts.length) {
    return null;
  }

  return (
    <div className={st(classes.root, { isMobile })}>
      <Text
        tagName={TagName.H3}
        type={TextType.Title}
        className={classes.title}
      >
        {title}
      </Text>
      <Text type={TextType.Primary} className={classes.subtitle}>
        {subtitle}
      </Text>
      <ProductsPicker
        className={classes.pickerRoot}
        products={inStockProducts.slice(0, maxItems)}
        selection={selection}
        onSelectionChange={handleSelectionChange}
        onModalOpen={onModalOpen}
        isLoadingProductDetails={isLoadingProductDetails}
        labels={{
          addButtonLabel: t('app.bookings-form.related-products.add-button'),
          modalTitle: t('app.bookings-form.related-products.modal.title'),
          closeAriaLabel: t('app.bookings-form.close-modal.aria-label'),
          chooseOptionLabel: t(
            'app.bookings-form.related-products.picker.choose-option',
          ),
          quantityLabel: t(
            'app.bookings-form.related-products.picker.quantity',
          ),
          modalLoadingLabel: t(
            'app.bookings-form.related-products.picker.modal-loading',
          ),
          decrementQuantityAriaLabel: t(
            'app.bookings-form.related-products.picker.decrement-quantity-aria-label',
          ),
          incrementQuantityAriaLabel: t(
            'app.bookings-form.related-products.picker.increment-quantity-aria-label',
          ),
          addToBookingLabel: (price) =>
            price ? `${ctaLabel} - ${price}` : ctaLabel,
        }}
      />
    </div>
  );
};
